'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Typography } from 'components';
import PasswordValidator from 'components/PasswordValidator';
import { PRIVACY_POLICY_LINK, TERMS_CONDITIONS_LINK } from 'constants/links';
import { SignupInput } from 'gql/graphql';
import { handlerEnter } from 'handlers/handlerEnter';
import { onErrorHelper } from 'handlers/handlerMutations';
import { toaster } from 'helpers/toaster';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useUserSignup } from 'providers/AuthProviders/useUserSignup';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { TGenericError } from 'types/TGenericError';
import { TScheme } from 'types/TScheme';
import * as yup from 'yup';
import YupPassword from 'yup-password';

YupPassword(yup);

export default function SignUp() {
  const router = useRouter();
  const t = useTranslations();
  const locale = useLocale();

  const schema = yup.object().shape({
    firstName: yup.string().required(t('messages.fields.first_name_required')),
    lastName: yup.string().required(t('messages.fields.last_name_required')),
    companyName: yup.string().optional(),
    email: yup
      .string()
      .email(t('messages.fields.email_validation'))
      .required(t('messages.fields.email_validation')),
    password: yup.string().password().required(t('messages.fields.password_required')),
    passwordConfirmation: yup
      .string()
      .required(t('messages.fields.confirm_password_required'))
      .test('passwordConfirmation', t('messages.fields.password_must_match'), function (value) {
        return this.parent.password === value;
      }),
    termsAccepted: yup
      .boolean()
      .is([true], t('messages.fields.accept_terms'))
      .required(t('messages.fields.accept_terms_required'))
  } as TScheme<SignupInput>);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    getValues
  } = useForm<SignupInput>({
    resolver: yupResolver(schema)
  });

  const password = watch('password');

  const { mutateAsync: userSignup } = useUserSignup({
    onSuccess: () => toaster.success(t('messages.success.sign_up')),
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const onFetch = useCallback(async () => {
    try {
      const data = getValues();
      await userSignup({ locale, ...data });

      const searchParams = new URLSearchParams();

      searchParams.set('email', data.email);
      searchParams.set('signUp', 'true');

      const url = '/auth/verification?' + searchParams;

      return router.push(url);
    } catch (error) {
      notifyException(error);
    }
  }, [getValues, userSignup, router]);

  const onSubmit = handleSubmit(onFetch);

  return (
    <form autoComplete="off" method="post" action="">
      <input autoComplete="false" name="hidden" type="text" className="hidden" />
      <div className="flex flex-col">
        <Typography variant="medium7xl" className={'text-tertiary-100'}>
          {t('auth.sign_up.title')}
        </Typography>
        <div className="pt-2">
          <div>
            <Typography variant="light4" className="text-tertiary-200">
              {t('auth.sign_up.fill_info')}
            </Typography>
            <Typography variant="light4" className="ml-1 text-tertiary-200">
              {t('auth.sign_up.select_message')}
            </Typography>
            <Typography variant="regular4" className="text-tertiary-200">
              <Link
                href="/auth/sign-in"
                className="ml-1 cursor-pointer text-sm text-secondary-link"
              >
                {t('auth.sign_up.login')}
              </Link>
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-1 pt-8">
        <div className="flex w-full gap-4">
          <ControlledInput
            control={control}
            error={errors.firstName}
            label={t('auth.sign_up.label_first_name')}
            type="text"
            name="firstName"
            classNameParent="w-full"
            placeholder={t('auth.sign_up.placeholder_first_name')}
          />
          <ControlledInput
            control={control}
            error={errors.lastName}
            label={t('auth.sign_up.label_last_name')}
            type="text"
            name="lastName"
            classNameParent="w-full"
            placeholder={t('auth.sign_up.placeholder_last_name')}
          />
        </div>
        <ControlledInput
          control={control}
          error={errors.companyName}
          label={t('auth.sign_up.label_company_name')}
          type="text"
          name="companyName"
          autoComplete="off"
          role="presentation"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_company_name')}
        />

        <ControlledInput
          control={control}
          error={errors.email}
          label={t('auth.sign_up.label_email')}
          type="text"
          name="email"
          autoComplete="new-garbage-one"
          role="presentation"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_email')}
        />

        <div>
          <ControlledInput
            control={control}
            error={errors.password}
            label={t('auth.sign_up.label_password')}
            type="password"
            name="password"
            autoComplete="new-password"
            role="presentation"
            classNameParent="w-full"
            placeholder={t('auth.sign_up.placeholder_password')}
          />
          <PasswordValidator password={password} />
        </div>

        <ControlledInput
          control={control}
          error={errors.passwordConfirmation}
          label={t('auth.sign_up.label_confirm_password')}
          type="password"
          name="passwordConfirmation"
          autoComplete="off"
          role="presentation"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_confirm_password')}
        />
        <ControlledInput
          control={control}
          error={errors.termsAccepted}
          type="checkbox"
          name="termsAccepted"
          label={
            <Typography variant="regular5">
              {t('auth.sign_up.agree_terms')}
              <Link
                href={TERMS_CONDITIONS_LINK}
                target="_blank"
                className="mx-1 cursor-pointer text-secondary-link"
              >
                {t('auth.sign_up.terms_conditions')}
              </Link>
              {t('auth.sign_up.and')}
              <Link
                href={PRIVACY_POLICY_LINK}
                target="_blank"
                className="ml-1 cursor-pointer text-secondary-link"
              >
                {t('auth.sign_up.privacy_policy')}
              </Link>
              .
            </Typography>
          }
        />
        <div className="w-full">
          <Button
            onClick={onSubmit}
            onKeyDown={handlerEnter(onSubmit)}
            label={t('auth.sign_up.submit')}
            className="w-full"
          />
        </div>
      </div>
    </form>
  );
}
